import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { IMAGES } from '../../assets/constant';
import { useGetUserProfileQuery } from '../../redux/complaintApi';
import { accountImageDisplayUrl } from '../../utils/accountImageUrl';
import {
  useGetEmailHistoriesQuery,
  useMarkAllEmailHistoriesReadMutation,
  useMarkEmailHistoryAsReadMutation,
} from '../../redux/emailHistoryApi';
import { isProbablyHtml, stripDangerousHtml } from '../../utils/notificationHtml';

function formatRelativeTime(iso) {
  if (iso == null || iso === '') return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffMs = Date.now() - t;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

function isAdminLike({ area, can }) {
  // IMPORTANT: "admin area" should be decided by role/area only.
  // Some roles (e.g. Manager) may have many permissions but must still use employee dashboard.
  return area === 'admin';
}

function leaveManagementPath({ area, roleKey }) {
  const isBackend = String(roleKey || '').toLowerCase() === 'backend';
  const isAdminArea = area === 'admin';
  if (isBackend) return '/dashboard/backend/leave-management';
  if (isAdminArea) return '/dashboard/admin/leave-management';
  return '/dashboard/employee/leave-management';
}

const SIDEBAR_NAV_ORDER = [
  'Dashboard',
  'Bill-To Flow',
  'Categories',
  'Sub Category',
  'Employees',
  'Helper Employees',
  'Behaviour Tracking',
  'Complaints',
  'Issue Type',
  'Leave Management',
  'Licences',
  'Outlets',
  'Product Category',
  'Product Sub Category',
  'Products',
];

function sortNavItemsBySidebarOrder(items) {
  return [...items].sort((a, b) => {
    const ai = SIDEBAR_NAV_ORDER.indexOf(a.label);
    const bi = SIDEBAR_NAV_ORDER.indexOf(b.label);
    const aIdx = ai === -1 ? SIDEBAR_NAV_ORDER.length : ai;
    const bIdx = bi === -1 ? SIDEBAR_NAV_ORDER.length : bi;
    return aIdx - bIdx;
  });
}

function buildNavItems({ area, can, roleKey }) {
  const items = [];
  const isBackend = String(roleKey || '').toLowerCase() === 'backend';
  const isAdminArea = isAdminLike({ area, can });

  if (can('dashboard')) {
    items.push({
      // Go directly to the correct dashboard to avoid double-navigation flicker.
      path: isBackend
        ? '/dashboard/backend'
        : isAdminArea
          ? '/dashboard/admin'
          : '/dashboard/employee',
      label: 'Dashboard',
      icon: 'home',
    });
  }

  if (can('leave_management')) {
    items.push({
      path: leaveManagementPath({ area, roleKey }),
      label: 'Leave Management',
      icon: 'calendar',
    });
  }

  if (can('complaints')) {
    items.push({
      path: isBackend
        ? '/dashboard/backend/complaints'
        : isAdminArea
          ? '/dashboard/admin/complaints'
          : '/dashboard/employee/complaints',
      label: 'Complaints',
      icon: 'complaints',
    });
  }

  // Admin modules should be driven by permissions (not by area).
  // Non-superadmin users can still have access to these modules.
  if (can('categories')) {
    items.push({ path: '/dashboard/admin/categories', label: 'Categories', icon: 'folder' });
    items.push({ path: '/dashboard/admin/sub-category', label: 'Sub Category', icon: 'folder' });
  }

  if (can('product_category')) {
    items.push({
      path: '/dashboard/admin/product-category',
      label: 'Product Category',
      icon: 'folder',
    });
    items.push({
      path: '/dashboard/admin/product-sub-category',
      label: 'Product Sub Category',
      icon: 'folder',
    });
  }

  if (can('employees')) {
    items.push({ path: '/dashboard/admin/employees', label: 'Employees', icon: 'people' });
  }

  if (can('outsource_employee')) {
    items.push({
      path: '/dashboard/admin/outsource-employee',
      label: 'Helper Employees',
      icon: 'people',
    });
  }

  if (can('issue_types')) {
    items.push({
      path: '/dashboard/admin/issue-type',
      label: 'Issue Type',
      icon: 'tag',
    });
  }

  if (can('outlets')) {
    items.push({ path: '/dashboard/admin/outlets', label: 'Outlets', icon: 'store' });
  }

  if (can('products')) {
    items.push({ path: '/dashboard/admin/products', label: 'Products', icon: 'products' });
  }

  if (can('licences')) {
    items.push({ path: '/dashboard/admin/licences', label: 'Licences', icon: 'licence' });
  }

  if (can('behaviour_tracking')) {
    items.push({
      path: '/dashboard/admin/behaviour-tracking',
      label: 'Behaviour Tracking',
      icon: 'behaviour',
    });
  }

  // items.push({
  //   path: '/dashboard/admin/bill-to-flow',
  //   label: 'Bill-To Flow',
  //   icon: 'billToFlow',
  //   children: [
  //     {
  //       label: 'My Tools',
  //       path: '/dashboard/admin/bill-to-flow',
  //       exact: true,
  //       alsoActiveFor: ['/dashboard/admin/bill-to-flow/create'],
  //     },
  //     { label: 'Daily Sales', path: '/dashboard/admin/bill-to-flow/daily-sales' },
  //   ],
  // });

  return sortNavItemsBySidebarOrder(items);
}

function NavIcon({ icon }) {
  const icons = {
    home: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    complaints: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
    folder: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      </svg>
    ),
    people: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    tag: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
    store: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    products: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </svg>
    ),
    licence: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
    calendar: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
    behaviour: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 20V10" />
        <path d="M18 20V4" />
        <path d="M6 20v-4" />
      </svg>
    ),
    billToFlow: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
  };
  return icons[icon] || icons.home;
}

function isNavChildActive(pathname, child) {
  if (Array.isArray(child.alsoActiveFor)) {
    if (child.alsoActiveFor.some((path) => pathname === path || pathname.startsWith(`${path}/`))) {
      return true;
    }
  }
  if (child.exact) return pathname === child.path;
  return pathname === child.path || pathname.startsWith(`${child.path}/`);
}

function isNavGroupActive(pathname, item) {
  if (!item.children?.length) return false;
  if (pathname === item.path || pathname.startsWith(`${item.path}/`)) return true;
  return item.children.some((child) => isNavChildActive(pathname, child));
}

function DashboardLayout({ children, title }) {
  const { user, logout, can, area, roleKey } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [billToFlowOpen, setBillToFlowOpen] = useState(() =>
    location.pathname.startsWith('/dashboard/admin/bill-to-flow')
  );

  const {
    data: emailHistoriesRes,
    isLoading: isEmailHistoriesLoading,
    isFetching: isEmailHistoriesFetching,
    error: emailHistoriesError,
    refetch: refetchEmailHistories,
  } = useGetEmailHistoriesQuery(undefined, {
    skip: !user,
    pollingInterval: 90_000,
  });
  const [markOneRead] = useMarkEmailHistoryAsReadMutation();
  const [markAllRead, { isLoading: isMarkAllReadLoading }] = useMarkAllEmailHistoriesReadMutation();

  const notifications = emailHistoriesRes?.data ?? [];
  const unreadCount = useMemo(
    () => notifications.filter((n) => n && !n.read).length,
    [notifications]
  );

  useEffect(() => {
    if (isNotificationsOpen && user) {
      refetchEmailHistories();
    }
  }, [isNotificationsOpen, user, refetchEmailHistories]);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith('/dashboard/admin/bill-to-flow')) {
      setBillToFlowOpen(true);
    }
  }, [location.pathname]);

  const employeeId = user?.employeeCode;
  const { data: userProfileRes } = useGetUserProfileQuery(employeeId, {
    skip: !employeeId,
  });
  const profile = userProfileRes?.data;
  const displayName = profile?.name || user?.name || 'User Name';
  const displayEmail = profile?.email || user?.email || 'user@dummyemail.com';
  const displayImage = accountImageDisplayUrl(profile, 'employee_image');

  const navItems = useMemo(
    () => buildNavItems({ area, can, roleKey }),
    [area, can, roleKey]
  );

  const handleLogout = () => {
    setIsLogoutConfirmOpen(true);
  };

  const confirmLogout = () => {
    setIsLogoutConfirmOpen(false);
    logout();
    toast.success('Logged out successfully.');
    navigate('/login');
  };

  const cancelLogout = () => {
    setIsLogoutConfirmOpen(false);
  };

  return (
    <div className="flex min-h-screen bg-[#F7E5D5]">
      {isSidebarOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          aria-label="Close menu"
          onClick={() => setIsSidebarOpen(false)}
        />
      ) : null}
      <aside
        className={`fixed left-0 top-0 z-50 flex h-screen w-[260px] min-w-0 flex-shrink-0 flex-col border-r border-[#e8e4df] bg-white transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Link to="/landing" className="flex items-center justify-center p-6">
          <img
            src={IMAGES.SIDEBAR_LOGO}
            alt="Grover Sweets"
            className="h-20 w-20 rounded-[10%] bg-[#F7E5D5] p-1 object-contain"
          />
        </Link>
        <nav className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2">
          {navItems.map((item) => {
            if (item.children?.length) {
              const groupActive = isNavGroupActive(location.pathname, item);
              return (
                <div key={item.path} className="mb-1">
                  <button
                    type="button"
                    onClick={() => setBillToFlowOpen((prev) => !prev)}
                    className={`flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-[0.95rem] font-medium transition ${
                      groupActive
                        ? 'text-brand'
                        : 'text-gray-700 hover:bg-[#F7E5D5] hover:text-brand'
                    }`}
                  >
                    <NavIcon icon={item.icon} />
                    <span className="flex-1 text-left">{item.label}</span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className={`transition-transform ${billToFlowOpen ? 'rotate-180' : ''}`}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {billToFlowOpen ? (
                    <div className="relative ml-6 mt-1 space-y-0.5 border-l border-gray-200 pl-4">
                      {item.children.map((child) => {
                        const childActive = isNavChildActive(location.pathname, child);
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`block rounded-lg py-2 pl-2 text-sm font-medium transition ${
                              childActive
                                ? 'text-brand'
                                : 'text-gray-600 hover:text-brand'
                            }`}
                          >
                            {child.label}
                          </Link>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            }

            const isDashboardItem = item.label === 'Dashboard';
            const isActive = isDashboardItem
              ? location.pathname === item.path
              : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`mb-1 flex items-center gap-3 rounded-lg px-4 py-2.5 text-[0.95rem] font-medium transition ${
                  isActive
                    ? 'bg-brand text-white'
                    : 'text-gray-700 hover:bg-[#F7E5D5] hover:text-brand'
                }`}
              >
                <NavIcon icon={item.icon} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="flex-shrink-0 border-t border-[#e8e4df] p-4">
          <div className="relative flex min-w-0 items-center gap-2 rounded-2xl bg-brand px-4 py-3 text-white">
            <Link
              to="/dashboard/profile"
              className="flex min-w-0 flex-1 items-center gap-3"
              title="View profile"
            >
              <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full bg-white/20">
                {displayImage ? (
                  <img
                    src={displayImage}
                    alt={displayName}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs font-semibold text-white/90">
                    {(displayName || 'U').slice(0, 1).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1 overflow-hidden">
                <p className="m-0 truncate text-sm font-semibold" title={displayName}>
                  {displayName}
                </p>
                <p className="m-0 truncate text-xs text-white/90" title={displayEmail}>
                  {displayEmail}
                </p>
              </div>
            </Link>
            <button
              type="button"
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
              onClick={handleLogout}
              title="Logout"
              aria-label="Logout"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </div>
      </aside>
      <div className="relative flex flex-1 flex-col overflow-hidden lg:ml-[260px]">
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-[#e8e4df] bg-[#F7E5D5] px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button
              type="button"
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-gray-700 hover:bg-white/60 lg:hidden"
              aria-label="Open menu"
              onClick={() => setIsSidebarOpen(true)}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <h1 className="truncate text-lg font-bold text-brand sm:text-xl">{title || 'Dashboard'}</h1>
          </div>
          <button
            type="button"
            className={`relative rounded-full p-2 text-gray-600 hover:text-brand ${
              isNotificationsOpen ? 'text-brand' : ''
            }`}
            aria-label="Notifications"
            onClick={() => setIsNotificationsOpen((prev) => !prev)}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {unreadCount > 0 ? (
              <span
                className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-red-600 ring-2 ring-[#F7E5D5]"
                aria-hidden
              />
            ) : null}
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8">
          {children}
        </div>
        {isNotificationsOpen && (
          <aside
            className="absolute right-0 top-0 z-30 flex h-full w-full max-w-md flex-col gap-2 border-l border-[#e8e4df] bg-white p-4 shadow-2xl sm:p-6"
            aria-label="Notifications panel"
          >
           <div className="mb-2 flex items-center justify-between gap-2">
  <h2 className="text-base font-semibold text-gray-800 pt-1">
    Notifications
  </h2>

  <div className="flex items-center gap-2">
    {unreadCount > 0 ? (
      <button
        type="button"
        className="text-sm font-semibold text-brand hover:underline disabled:opacity-50 pt-1"
        disabled={isMarkAllReadLoading}
        onClick={async () => {
          try {
            await markAllRead().unwrap();
            toast.success('All notifications marked as read.');
          } catch {
            toast.error('Could not mark all as read.');
          }
        }}
      >
        Mark all read
      </button>
    ) : null}

    <button
      type="button"
      className="flex h-8 w-8 items-center justify-center text-3xl text-brand"
      aria-label="Close notifications"
      onClick={() => setIsNotificationsOpen(false)}
    >
      ×
    </button>
  </div>
</div>
            {emailHistoriesError ? (
              <p className="text-sm text-red-600">
                Could not load notifications. Please try again later.
              </p>
            ) : null}
            {isEmailHistoriesLoading && !emailHistoriesRes ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : null}
            <ul className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
              {!isEmailHistoriesLoading && !emailHistoriesError && notifications.length === 0 ? (
                <li className="text-sm text-gray-500">No notifications yet.</li>
              ) : null}
              {notifications.map((row) => {
                const key = row.id != null ? String(row.id) : JSON.stringify(row);
                const relative = formatRelativeTime(row.createdAt);
                const unread = !row.read;
                return (
                  <li key={key}>
                    <button
                      type="button"
                      className={`flex w-full items-start justify-between gap-3 rounded-lg border px-3 py-3 text-left transition hover:opacity-95 ${
                        unread
                          ? 'border-slate-700/25 bg-slate-800/12 text-gray-900 shadow-sm ring-1 ring-slate-800/20'
                          : 'border-transparent bg-[#faf8f5] text-gray-600'
                      } ${isEmailHistoriesFetching ? 'opacity-70' : ''}`}
                      onClick={async () => {
                        if (!unread || row.id == null) return;
                        try {
                          await markOneRead(row.id).unwrap();
                          toast.success('Notification marked as read.');
                        } catch {
                          toast.error('Could not mark notification as read.');
                        }
                      }}
                    >
                      <div
                        className={`min-w-0 flex-1 text-left text-sm leading-snug ${
                          unread ? 'text-gray-900' : 'text-gray-600'
                        }`}
                      >
                        {row.notifySubject ? (
                          <p className="m-0 font-bold leading-snug text-gray-900">{row.notifySubject}</p>
                        ) : null}
                        {row.notifyContent ? (
                          isProbablyHtml(row.notifyContent) ? (
                            <div
                              className="notification-email-html m-0 mt-1 font-normal text-gray-700 [&_h1]:m-0 [&_h1]:text-sm [&_h1]:font-normal [&_h2]:m-0 [&_h2]:text-sm [&_h2]:font-normal [&_p]:my-1.5 [&_p]:font-normal [&_strong]:font-semibold"
                              dangerouslySetInnerHTML={{
                                __html: stripDangerousHtml(row.notifyContent),
                              }}
                            />
                          ) : (
                            <p className="m-0 mt-1 whitespace-pre-line font-normal text-gray-700">
                              {row.notifyContent}
                            </p>
                          )
                        ) : null}
                        {!row.notifySubject && !row.notifyContent ? (
                          <p className="m-0 font-normal text-gray-500">—</p>
                        ) : null}
                      </div>
                      {relative ? (
                        <span
                          className={`flex-shrink-0 whitespace-nowrap text-xs ${
                            unread ? 'text-slate-600' : 'text-gray-400'
                          }`}
                        >
                          {relative}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>
        )}
      </div>

      {isLogoutConfirmOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Logout confirmation"
          onClick={cancelLogout}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900">Logout</h2>
            <p className="mt-2 text-sm text-gray-600">
              Are you sure you want to logout?
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={cancelLogout}
                className="rounded-lg border border-[#e8e4df] bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-[#faf8f5]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmLogout}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardLayout;
